// Lightweight session storage helper that namespaces keys per logged-in user.
// This prevents cross-account leakage when multiple accounts are used in the same browser session.
export function getUserIdFromToken() {
  try {
    const token = localStorage.getItem("authToken")
    if (!token) return "anon"
    const parts = token.split('.')
    if (parts.length < 2) return 'anon'
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    // return a stable identifier if available
    return payload.user_id || payload.sub || payload.id || payload.email || 'anon'
  } catch (err) {
    return 'anon'
  }
}

export function namespacedKey(key) {
  const uid = getUserIdFromToken()
  return `${key}::${uid}`
}

export function getSessionItem(key) {
  try {
    const v = sessionStorage.getItem(namespacedKey(key))
    if (v === null) return null
    return JSON.parse(v)
  } catch (err) {
    // fallback: return raw value
    return sessionStorage.getItem(namespacedKey(key))
  }
}

export function setSessionItem(key, value) {
  try {
    sessionStorage.setItem(namespacedKey(key), JSON.stringify(value))
  } catch (err) {
    // best-effort: try to store string
    try {
      sessionStorage.setItem(namespacedKey(key), String(value))
    } catch (e) {
      console.warn('Failed to set session item', key, e)
    }
  }
}

export function removeSessionItem(key) {
  try {
    sessionStorage.removeItem(namespacedKey(key))
  } catch (err) {
    // noop
  }
}

export function getLocalItem(key) {
  try {
    const v = localStorage.getItem(namespacedKey(key))
    if (v === null) return null
    return JSON.parse(v)
  } catch (err) {
    return localStorage.getItem(namespacedKey(key))
  }
}

export function setLocalItem(key, value) {
  try {
    localStorage.setItem(namespacedKey(key), JSON.stringify(value))
  } catch (err) {
    try {
      localStorage.setItem(namespacedKey(key), String(value))
    } catch (e) {
      console.warn('Failed to set local item', key, e)
    }
  }
}

export function removeLocalItem(key) {
  try {
    localStorage.removeItem(namespacedKey(key))
  } catch (err) {
    // noop
  }
}

// Stable per-browser identifier, deliberately NOT namespaced by auth state
// (unlike the helpers above) since it must exist and stay the same before
// any token is issued and across repeated guest logins.
export function getOrCreateDeviceId() {
  const key = "deviceId"
  try {
    let deviceId = localStorage.getItem(key)
    if (!deviceId) {
      deviceId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      localStorage.setItem(key, deviceId)
    }
    return deviceId
  } catch (err) {
    return null
  }
}
